import type { estypes } from '@elastic/elasticsearch';

import { BOOLEAN_FACET_KEYS } from '~/search-domain/constants/booleanFacetKeys';
import { type PremiumMarketMoversResult } from '~/search-domain/pipelines/premium-market-movers/types';
import {
  type Loggable,
  type SearchDomainAggregations,
  type SearchDomainHitWithFields,
  type SearchDomainHitWithSource,
} from '~/search-domain/types';
import { type BettingInsightsElasticSearchContext, type ComputedAttributes } from '~/search-domain/types/contracts';
import { replaceResponseHits } from '~/search-domain/utils';
import { formatInTimeZoneWithLocale } from '~/search-domain/utils/response-factories';

export type OriginalAggregations = {
  unique_bets: UniqueBetsAggregation;
};

export type UniqueBetsAggregation = {
  value: number;
};

export type PremiumMarketMoversSearchResponseBody = estypes.SearchResponseBody<
  PremiumMarketMoversResult,
  SearchDomainAggregations
>;

export type AggregatedPremiumMarketMoversSearchResponseBody = estypes.SearchResponseBody<
  PremiumMarketMoversResult & ComputedAttributes,
  SearchDomainAggregations
>;

export type StrippedPremiumMarketMoversResult = Omit<
  PremiumMarketMoversResult,
  'oddsAmerican' | 'oddsDecimal' | 'oddsFractional'
>;

export type StrippedPremiumMarketMoversSearchResponseBody = estypes.SearchResponseBody<
  StrippedPremiumMarketMoversResult,
  SearchDomainAggregations
>;

export type ShortestOdds = {
  oddsAmerican: string;
  oddsDecimal: number;
  oddsFractional: string;
};

type PremiumMarketMoversHit = estypes.SearchHit<PremiumMarketMoversResult>;
type PremiumMarketMoversHitWithSource = SearchDomainHitWithSource<PremiumMarketMoversResult>;
type PremiumMarketMoversInnerHits = Record<
  string,
  { hits: { hits: Array<estypes.SearchHit<PremiumMarketMoversResult>> } }
>;
type PremiumMarketMoversHitWithFields = SearchDomainHitWithFields<
  PremiumMarketMoversResult,
  { betId: number[]; shortestOdds?: ShortestOdds }
>;
type AggregatedHit = estypes.SearchHit<PremiumMarketMoversResult & ComputedAttributes> & {
  _source: PremiumMarketMoversResult & ComputedAttributes;
};

export async function createResponseFactory(
  this: Loggable,
  config: BettingInsightsElasticSearchContext,
  searchResponse: PremiumMarketMoversSearchResponseBody,
): Promise<AggregatedPremiumMarketMoversSearchResponseBody | StrippedPremiumMarketMoversSearchResponseBody> {
  const mappedSearchResponse = mapBooleanFacetBuckets(searchResponse);
  const aggregations = mappedSearchResponse.aggregations as OriginalAggregations;
  if (isFacetOnlyResponse(aggregations)) {
    return mappedSearchResponse as AggregatedPremiumMarketMoversSearchResponseBody;
  }

  const { unique_bets: uniqueBets, ...baseAggregations } = aggregations;
  const shortestOddsByBetId = collectShortestOddsByBetId(searchResponse.hits.hits);
  const aggregatedHits = buildAggregatedHits(searchResponse.hits.hits, shortestOddsByBetId, config.userPreference);

  return replaceResponseHits(mappedSearchResponse, {
    aggregations: baseAggregations as SearchDomainAggregations,
    hits: aggregatedHits,
    isWalled: config.isWalled,
    totalRelation: 'eq',
    totalValue: uniqueBets.value,
  });
}

function isFacetOnlyResponse(aggregations: OriginalAggregations) {
  const aggregationKeys = Object.keys(aggregations || {});
  return aggregationKeys.length === 1 && !aggregations?.unique_bets;
}

function getInnerHits(hit: PremiumMarketMoversHitWithSource): PremiumMarketMoversInnerHits | undefined {
  return hit.inner_hits as PremiumMarketMoversInnerHits | undefined;
}

function toShortestOdds(source: PremiumMarketMoversResult): ShortestOdds {
  return {
    oddsAmerican: source.oddsAmerican,
    oddsDecimal: source.oddsDecimal,
    oddsFractional: source.oddsFractional,
  };
}

function collectShortestOddsByBetId(hits: PremiumMarketMoversHit[]): Map<number, ShortestOdds> {
  return hits.filter(isHitWithSource).reduce((map, hit) => {
    const shortestOddsSource = getInnerHits(hit)?.shortest_odds?.hits?.hits?.[0]?._source;
    if (!shortestOddsSource?.betId) return map;
    map.set(shortestOddsSource.betId, toShortestOdds(shortestOddsSource));
    return map;
  }, new Map<number, ShortestOdds>());
}

function toBestOddsHit(hit: PremiumMarketMoversHitWithSource): PremiumMarketMoversHitWithSource | null {
  const bestOddsSource = getInnerHits(hit)?.best_odds?.hits?.hits?.[0]?._source;
  if (!bestOddsSource) return null;
  return {
    ...hit,
    _source: bestOddsSource,
  };
}

function isHitWithSource(hit: PremiumMarketMoversHit | null): hit is PremiumMarketMoversHitWithSource {
  return !!hit?._source;
}

function addBetFields(
  hit: PremiumMarketMoversHitWithSource,
  shortestOddsByBetId: Map<number, ShortestOdds>,
): PremiumMarketMoversHitWithFields {
  const betId = hit._source.betId;
  return {
    ...hit,
    fields: {
      betId: betId ? [betId] : [],
      shortestOdds: betId ? shortestOddsByBetId.get(betId) : undefined,
    },
  };
}

function buildAggregatedHits(
  hits: PremiumMarketMoversHit[],
  shortestOddsByBetId: Map<number, ShortestOdds>,
  userPreference: { TZ: string; locale: string },
): AggregatedHit[] {
  return hits
    .filter(isHitWithSource)
    .map(toBestOddsHit)
    .filter(isHitWithSource)
    .map(hit => addBetFields(hit, shortestOddsByBetId))
    .map(mapHitsToPremiumMarketMoversResults(userPreference));
}

function mapHitsToPremiumMarketMoversResults({ TZ, locale }: { TZ: string; locale: string }) {
  return (hit: PremiumMarketMoversHitWithFields): AggregatedHit => {
    return {
      ...hit,
      _source: {
        ...hit._source,
        subeventStartDate: formatInTimeZoneWithLocale(hit._source.subeventStartTime, 'yyyy-MM-dd', TZ, locale),
        shortestOdds: hit.fields.shortestOdds || {
          oddsAmerican: '',
          oddsDecimal: 0,
          oddsFractional: '',
        },
      },
      fields: {
        betId: hit.fields.betId[0],
      },
    };
  };
}

const booleanFacetKeySet = new Set<string>(BOOLEAN_FACET_KEYS);

function mapBooleanFacetBuckets(searchResponse: PremiumMarketMoversSearchResponseBody) {
  const aggregations = searchResponse.aggregations as Record<string, unknown> | undefined;
  if (!aggregations) return searchResponse;

  let didChange = false;
  const mappedAggregations = Object.fromEntries(
    Object.entries(aggregations).map(([aggregationKey, aggregationValue]) => {
      if (!booleanFacetKeySet.has(aggregationKey)) {
        return [aggregationKey, aggregationValue];
      }
      if (!aggregationValue || typeof aggregationValue !== 'object') {
        return [aggregationKey, aggregationValue];
      }

      const buckets = (aggregationValue as { buckets?: unknown[] }).buckets;
      if (!Array.isArray(buckets)) {
        return [aggregationKey, aggregationValue];
      }

      const mappedBuckets = buckets.map(bucket => {
        if (!bucket || typeof bucket !== 'object') return bucket;
        const key = (bucket as { key?: unknown }).key;
        if (key !== '0' && key !== 0 && key !== '1' && key !== 1) return bucket;

        didChange = true;
        const normalizedKey = key === '1' || key === 1 ? 'true' : 'false';
        return {
          ...(bucket as Record<string, unknown>),
          key: normalizedKey,
          key_as_string: normalizedKey,
        };
      });

      return [
        aggregationKey,
        {
          ...(aggregationValue as Record<string, unknown>),
          buckets: mappedBuckets,
        },
      ];
    }),
  );

  if (!didChange) return searchResponse;

  return {
    ...searchResponse,
    aggregations: mappedAggregations as PremiumMarketMoversSearchResponseBody['aggregations'],
  };
}
